import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  WritableSignal,
} from '@angular/core'
import { CommonModule, KeyValuePipe } from '@angular/common'
import { CardModule } from 'primeng/card'
import { CheckboxModule } from 'primeng/checkbox'
import { BadgeModule } from 'primeng/badge'
import { MessageModule } from 'primeng/message'
import { ScrollPanelModule } from 'primeng/scrollpanel'
import { TooltipModule } from 'primeng/tooltip'
import { ButtonModule } from 'primeng/button'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { AffairInformationsComponent } from '../affair/affair-informations/affair-informations.component'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../../../../environments/environment'
import { AffairOfficeFileComponent } from './affair-office-file/affair-office-file.component'

export interface ErpValue {
  administrativeInternalNote?: string
  id: number
  erpSource: string
  userAssistant: {
    lastname: string
    firstname: string
  }
  userTechnician: {
    lastname: string
    firstname: string
  }
}

export interface Contact {
  comment: string
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
}

@Component({
  selector: 'app-affair-schedule-products-select',
  templateUrl: './affair-schedule-products-select.component.html',
  styleUrls: ['./affair-schedule-products-select.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    CheckboxModule,
    BadgeModule,
    MessageModule,
    ScrollPanelModule,
    TooltipModule,
    ButtonModule,
    ProgressSpinnerModule,
    KeyValuePipe,
    AffairInformationsComponent,
    AffairOfficeFileComponent,
  ],
})
export class AffairScheduleProductsSelectComponent
  implements OnInit, OnChanges
{
  @Input() affair: any
  @Input() products: any[] = []
  @Input() loadingProducts: boolean = false

  // Add inputs for restored data
  @Input() restoredSelectedProductIds: number[] = []
  @Input() restoredProductGroup: any = null

  // Add a reset flag input
  @Input() resetSelection: boolean = false

  @Output() productSelectionChanged = new EventEmitter<any[]>()
  @Output() productGroupCreated = new EventEmitter<any>()

  public selectedProductIds: Set<number> = new Set()
  public productGroup: any = null
  private isInitialized = false

  protected noteDuplicates: boolean = false

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    // Initialize from restored data or default
    this.initializeComponent()
    this.getContactsAffair()
    this.getFullAffairDetails()
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the reset flag has been activated
    if (
      changes['resetSelection'] &&
      changes['resetSelection'].currentValue === true
    ) {
      // Clear selection
      this.selectedProductIds.clear()
      this.productGroup = null
      this.isInitialized = false // Reset the initialization flag

      // Emit empty selection
      this.productSelectionChanged.emit([])
      this.productGroupCreated.emit(null)
      return
    }

    // If products have been loaded or restored data changed, reinitialize
    if (
      changes['products']?.currentValue ||
      changes['restoredSelectedProductIds']?.currentValue ||
      changes['restoredProductGroup']?.currentValue
    ) {
      this.initializeComponent()
    }
  }

  /**
   * Initialize the component with either restored data or default selection
   */
  private initializeComponent(): void {
    // Always proceed with initialization if:
    // 1. We haven't initialized yet, OR
    // 2. We have no selected products, OR
    // 3. We have new restored data
    if (
      !this.isInitialized ||
      this.selectedProductIds.size === 0 ||
      this.hasNewRestoredData()
    ) {
      // First clear existing state
      this.selectedProductIds.clear()

      // If we have restored data, use it
      if (this.hasRestoredData()) {
        this.restoreFromInput()
      } else {
        // No restored data, set up default selection (including single product)
        this.setupDefaultSelection()
      }

      this.isInitialized = true
    }
  }

  /**
   * Check if we have any restored data to use
   */
  private hasRestoredData(): boolean {
    // Only return true if we have actual valid data
    return (
      this.restoredSelectedProductIds?.length > 0 ||
      (this.restoredProductGroup &&
        this.restoredProductGroup.products?.length > 0)
    )
  }

  /**
   * Check if we have new restored data that's different from current state
   */
  private hasNewRestoredData(): boolean {
    if (!this.hasRestoredData()) return false

    // Check if selected IDs are different
    if (
      this.restoredSelectedProductIds?.length !== this.selectedProductIds.size
    )
      return true

    // Check if group is different
    if (this.restoredProductGroup?.id !== this.productGroup?.id) return true

    return false
  }

  /**
   * Restore component state from input properties with proper typing
   */
  private restoreFromInput(): void {
    // Clear any existing selections first
    this.selectedProductIds.clear()

    // First priority: use restored selected product IDs if available
    if (this.restoredSelectedProductIds?.length) {
      this.restoredSelectedProductIds.forEach((id) =>
        this.selectedProductIds.add(Number(id))
      )
    }
    // Second priority: use product IDs from restored group if available
    else if (this.restoredProductGroup?.products?.length) {
      // Extract product IDs from the restored group and add them to selectedProductIds
      this.restoredProductGroup.products.forEach((product: any) => {
        if (product && product.id) {
          this.selectedProductIds.add(Number(product.id))
        }
      })
    }

    // Restore product group
    if (this.restoredProductGroup) {
      this.productGroup = this.restoredProductGroup
    }
    // Or create group from selected IDs
    else if (this.selectedProductIds.size > 0) {
      this.updateProductGroup()
    }

    // Ensure we emit the selected products
    this.emitSelectionChanged()
  }

  /**
   * Sets up the default product selection
   * - If 2+ products exist, selects and groups the first 2
   * - If only 1 product exists, selects just that one
   */
  private setupDefaultSelection(): void {
    if (!this.products || !this.products.length) return

    // Clear any existing selections first to avoid duplicates
    this.selectedProductIds.clear()

    if (this.products.length === 1) {
      // If there's only one product, select it automatically
      const singleProduct = this.products[0]
      this.selectedProductIds.add(Number(singleProduct.id))
      this.updateProductGroup()
    }

    this.emitSelectionChanged()
  }

  toggleProductSelection(productId: number, event?: Event): void {
    if (event) {
      event.stopPropagation()
    }

    const numericId = Number(productId)
    if (this.selectedProductIds.has(numericId)) {
      // Before removing, check if this would leave us with no selections
      if (this.selectedProductIds.size <= 1) {
        // Alert user they must keep at least one product selected
        alert('Au moins un produit doit être sélectionné')
        return
      }
      this.selectedProductIds.delete(numericId)
    } else {
      this.selectedProductIds.add(numericId)
    }

    // Automatically update the group when selections change
    this.updateProductGroup()
    this.emitSelectionChanged()
  }

  /**
   * Update the product group based on current selections
   */
  private updateProductGroup(): void {
    const selectedProducts = this.products.filter((p: any) =>
      this.selectedProductIds.has(Number(p.id))
    )

    if (selectedProducts.length < 1) return

    // Create unique ID for the group
    const timestamp = Date.now()
    const productIdsList = selectedProducts
      .map((p: any) => p.id)
      .sort()
      .join('_')
    const groupId = `group_${timestamp}_${productIdsList}`

    // Create or update the product group
    this.productGroup = {
      id: groupId,
      products: JSON.parse(JSON.stringify(selectedProducts)),
      productsNames: this.formatGroupName(selectedProducts),
      totalDuration: selectedProducts.reduce(
        (sum: number, p: any) => sum + (p.product.duration || 0),
        0
      ),
      isDefaultGroup: true,
    }

    // Emit the updated group to parent component
    this.productGroupCreated.emit(this.productGroup)
  }

  private formatGroupName(products: any[]): string {
    return (
      products
        .map((p) => p.product.name)
        .join(', ')
        .substring(0, 60) + (products.length > 2 ? '...' : '')
    )
  }

  isProductSelected(productId: number): boolean {
    return this.selectedProductIds.has(Number(productId))
  }

  isProductInDefaultGroup(productId: number): boolean {
    // All selected products are part of the group
    return this.isProductSelected(productId)
  }

  getProductIdsString(products: any[]): string {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return ''
    }
    return products
      .map((product) => product?.id?.toString() || '')
      .filter((id) => id)
      .join(',')
  }

  emitSelectionChanged(): void {
    const selectedProducts = this.products.filter((p: any) =>
      this.selectedProductIds.has(Number(p.id))
    )

    if (selectedProducts.length === 0) {
      console.warn('No products selected, cannot proceed')
    }

    this.productSelectionChanged.emit(selectedProducts)
  }

  getFilteredErpEntries(): { key: string; value: ErpValue }[] {
    const entries = this.getErpEntries()

    if (!this.noteDuplicates) {
      return entries
    }

    const seenNotes = new Set<string>()
    const filtered: { key: string; value: ErpValue }[] = []

    for (const entry of entries) {
      if (!entry.value) continue

      const note = entry.value.administrativeInternalNote

      if (note && seenNotes.has(note)) {
        // Skip duplicate note
        continue
      }

      if (note) {
        seenNotes.add(note)
      }

      filtered.push(entry)
    }

    return filtered
  }

  getErpEntries(): { key: string; value: ErpValue }[] {
    if (!this.affair || !this.affair.srcErp) return []

    const srcErp = this.affair.srcErp as Record<string, ErpValue | null>

    const entries = Object.entries(srcErp)
      .filter(([_, value]) => value !== null)
      .map(([key, value]) => ({
        key,
        value: value as ErpValue,
      }))

    const seenNotes = new Set<string>()
    let hasDuplicates = false

    for (const entry of entries) {
      const note = entry.value.administrativeInternalNote

      if (!note) continue

      if (seenNotes.has(note)) {
        hasDuplicates = true
        break
      }

      seenNotes.add(note)
    }

    this.noteDuplicates = hasDuplicates
    return entries
  }

  /**
   * Get the ERP-specific CSS class safely
   * @param key The ERP source key
   * @returns Safe CSS class name
   */
  getErpCssClass(key: string): string {
    return key ? `text-${key}` : ''
  }

  /**
   * Get the ERP-specific logo class safely
   * @param key The ERP source key
   * @returns Safe logo class name
   */
  getLogoClass(key: string): string {
    return key ? `fa-logo-${key}` : ''
  }

  protected affairContacts: Contact[] = []
  fullAffairDetails: any
  fullAffairLoading: boolean = false

  getContactsAffair() {
    if (!this.affair) return []

    this.http
      .get(
        environment.planifyApiUrl +
          '/affairs/' +
          this.affair.mlRef +
          '/affair-contacts'
      )
      .subscribe({
        next: (data: any) => {
          this.affairContacts = data
        },
        error: (error) => {
          console.error('Error fetching affair contacts:', error)
          return []
        },
      })
    return []
  }

  private getFullAffairDetails() {
    if (!this.affair) return

    this.fullAffairLoading = true
    this.http
      .get(environment.planifyApiUrl + '/affairs/' + this.affair.mlRef)
      .subscribe({
        next: (data: any) => {
          this.fullAffairDetails = data
          this.fullAffairLoading = false
        },
        error: (error) => {
          console.error('Error fetching full affair details:', error)
          this.fullAffairLoading = false
        },
      })
  }

  /**
   * Get warnings for missing ERP entries
   * Returns a list of warning messages for ERPs that are null in srcErp
   */
  getMissingErpWarnings(): string[] {
    if (!this.affair || !this.affair.srcErp) return []

    const warnings: string[] = []
    const srcErp = this.affair.srcErp as Record<string, ErpValue | null>

    const erpNames: Record<string, string> = {
      abm: 'ABM',
      nrj: 'NRJ',
      bb: 'BB',
    }

    for (const [key, value] of Object.entries(srcErp)) {
      if (value === null) {
        const erpDisplayName = erpNames[key] || key.toUpperCase()
        warnings.push(
          `Attention l'affaire n'est pas synchronisée sur l'ERP ${erpDisplayName}, par conséquent il n'est pas possible de programmer sur un technicien de la société ${erpDisplayName}`
        )
      }
    }

    return warnings
  }
}
